import { useEffect, useState } from "react";
import { createCheckout, razorpayConfig, razorpayCheckout, razorpayVerify } from "@/lib/api";
import { CreditCard, Smartphone, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

// Loads the Razorpay Checkout.js script exactly once
let _rzpLoaded = false;
function loadRazorpay() {
  return new Promise((resolve) => {
    if (_rzpLoaded && window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => { _rzpLoaded = true; resolve(true); };
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

/**
 * PaymentChooser
 * Props:
 *  - orderId: string
 *  - priceUsd: number
 *  - onPaid: () => void       (called on Razorpay success)
 *  - onCancelText?: string    optional label for cancel action
 */
export default function PaymentChooser({ orderId, priceUsd, onPaid }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rzp, setRzp] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    razorpayConfig().then(setRzp).catch(() => setRzp({ configured: false }));
  }, []);

  const payStripe = async () => {
    setBusy("stripe");
    try {
      const { checkout_url } = await createCheckout(orderId);
      window.location.href = checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Stripe checkout failed");
      setBusy("");
    }
  };

  const payRazorpay = async () => {
    setBusy("razorpay");
    try {
      const loaded = await loadRazorpay();
      if (!loaded) { toast.error("Couldn't load Razorpay. Check your connection."); setBusy(""); return; }
      const co = await razorpayCheckout(orderId);
      const rp = new window.Razorpay({
        key: co.key_id,
        amount: co.amount_paise,
        currency: co.currency,
        order_id: co.razorpay_order_id,
        name: "Noxi",
        description: co.gig_title?.slice(0, 60) || "Gig order",
        image: "/favicon.png",
        prefill: {
          name: co.buyer.name,
          email: co.buyer.email,
        },
        theme: { color: "#F5C542" },
        method: { upi: true, card: true, netbanking: true, wallet: true },
        modal: {
          ondismiss: () => setBusy(""),
        },
        handler: async (resp) => {
          try {
            await razorpayVerify({
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment successful");
            if (onPaid) onPaid();
            else navigate(`/order/${orderId}`);
          } catch (e) {
            toast.error("Payment verification failed");
          } finally { setBusy(""); }
        },
      });
      rp.on("payment.failed", (resp) => {
        toast.error(resp?.error?.description || "Payment failed");
        setBusy("");
      });
      rp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Razorpay checkout failed");
      setBusy("");
    }
  };

  const inr = rzp ? Math.round(priceUsd * (rzp.usd_to_inr || 83)) : null;

  return (
    <div className="space-y-3" data-testid="payment-chooser">
      <div className="overline">◆ Choose payment method</div>

      {/* Razorpay */}
      {rzp?.configured && (
        <button
          data-testid="pay-razorpay-btn"
          onClick={payRazorpay}
          disabled={busy !== ""}
          className="w-full border-2 border-foreground bg-card hover:bg-foreground hover:text-background transition-colors p-4 flex items-center justify-between text-left disabled:opacity-40"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary text-primary-foreground flex items-center justify-center">
              <Smartphone className="w-5 h-5" strokeWidth={1.75}/>
            </div>
            <div>
              <div className="font-semibold text-sm">UPI / Cards / Netbanking</div>
              <div className="mono text-xs opacity-70">Powered by Razorpay · India</div>
            </div>
          </div>
          <div className="text-right">
            <div className="mono font-bold text-lg">₹{inr?.toLocaleString("en-IN")}</div>
            <div className="mono text-[10px] opacity-70">≈ ${priceUsd}</div>
          </div>
          {busy === "razorpay" && <Loader2 className="w-4 h-4 animate-spin ml-2"/>}
        </button>
      )}

      {/* Stripe */}
      <button
        data-testid="pay-stripe-btn"
        onClick={payStripe}
        disabled={busy !== ""}
        className="w-full border-2 border-foreground/30 bg-card hover:border-foreground transition-colors p-4 flex items-center justify-between text-left disabled:opacity-40"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 border border-foreground/30 flex items-center justify-center">
            <CreditCard className="w-5 h-5" strokeWidth={1.75}/>
          </div>
          <div>
            <div className="font-semibold text-sm">Credit / Debit Card</div>
            <div className="mono text-xs text-muted-foreground">Powered by Stripe · Global</div>
          </div>
        </div>
        <div className="text-right">
          <div className="mono font-bold text-lg">${priceUsd}</div>
          <div className="mono text-[10px] text-muted-foreground">USD</div>
        </div>
        {busy === "stripe" && <Loader2 className="w-4 h-4 animate-spin ml-2"/>}
      </button>

      <p className="mono text-[10px] text-muted-foreground text-center pt-1">
        SECURE CHECKOUT · YOUR PAYMENT IS ESCROWED UNTIL DELIVERY
      </p>
    </div>
  );
}
